<template>
    <div class="generate-container">
        <div class="form-area">
            <div class="field">
                <div class="label">1.选择合同类型：</div>
                <type-selector
                    v-model="type"
                    :disabled="isGenerating"
                    @change="query = ''"
                />
            </div>
            <div class="field">
                <div class="label">2.合同具体要求（选填）：</div>
                <veui-textarea
                    v-model="query"
                    :disabled="isGenerating"
                    :maxlength="maxlength"
                    placeholder="请输入具体的合同要求"
                    autoresize
                    @focus="onFocus"
                />
                <div class="tips">
                    可补充定制化的诉求，如“婚姻家庭”类文书可补充：针对离婚孩子抚养
                    权的协议提供范本
                </div>
            </div>
            <div
                class="submit-btn"
                :class="isGenerating || !surplus ? 'disabled' : ''"
                @click="handleGenerate"
            >
                <div class="btn-text">
                    {{ isGenerating ? "正在生成" : "开始生成" }}
                </div>
                <div class="btn-tip">
                    {{
                        isGenerating
                            ? "请稍等"
                            : "今日还可生成" + surplus + "次"
                    }}
                </div>
            </div>
        </div>
        <div class="result-area">
            <!-- 生成状态 -->
            <div v-if="isGenerating" class="result-step">
                <i class="generating-pic"></i>
                <span>生成中 {{ progressValue | percentFormat }}</span>
                <veui-loading-bar
                    ref="loadingBar"
                    :loading="isGenerating"
                    ui="m"
                />
            </div>
            <!-- 合同生成结果展示 -->
            <div
                v-else-if="contractInfo && +contractInfo.status === 1"
                class="result-wrapper"
            >
                <div class="header">
                    <h3>根据您的要求生成的AI合同</h3>
                    <span
                        v-if="contractInfo.update_time" class="time"
                    >生成时间：{{
                        contractInfo.update_time | dateFormat
                    }}</span>
                    <div class="download-btn" @click="handleDownload">
                        下载word
                    </div>
                </div>
                <div class="content">{{ contractInfo.contract_content }}</div>
            </div>
            <!-- 失败状态 -->
            <div v-else-if="failText" class="result-step">
                <i class="fail-pic"></i>
                <span>{{ failText }}</span>
            </div>
            <!-- 空状态 -->
            <div v-else class="empty">
                <i class="empty-pic"></i>
                <span>请在左侧选择类型后，点击“生成”</span>
            </div>
        </div>
        <veui-dialog
            overlay-class="confirm-veui-dialog"
            :open.sync="isShowFailDialog"
            closable
            ok-label="我知道了"
            @ok="isShowFailDialog = false"
        >
            <p>
                {{ failText }}
            </p>
        </veui-dialog>
    </div>
</template>
<script>
/**
 * @file 生成合同页面
 */
import {mapState, mapMutations, mapActions} from 'vuex';
import {Textarea, LoadingBar, Dialog} from 'veui';
import typeSelector from '../components/typeSelector.vue';
import {
    percentFormat,
    dateFormat,
    TYPE_LIST,
    getContractDocLink
} from '../util';
import {downloadFile} from '@/utils/download';

export default {
    components: {
        'veui-textarea': Textarea,
        'veui-loading-bar': LoadingBar,
        'veui-dialog': Dialog,
        'type-selector': typeSelector
    },
    filters: {
        percentFormat,
        dateFormat
    },
    data() {
        return {
            isGenerating: false,
            isDownloading: false,
            type: TYPE_LIST[0],
            maxlength: 800,
            query: '',
            surplus: 0,
            // 进度条数字
            progressValue: 0,
            getProgressTimer: null,
            // 创建时失败信息
            createFailText: '',
            isShowFailDialog: false
        };
    },
    watch: {
        generatingId: {
            handler(val) {
                this.isGenerating = !!val;
            },
            immediate: true
        },
        isGenerating: {
            handler(val) {
                if (val) {
                    this.getProgressValue();
                }
                else {
                    clearTimeout(this.getProgressTimer);
                }
            },
            immediate: true
        },
        latestId(val) {
            if (val) {
                this.getGenerateCount();
            }
        },
        contractInfo(val) {
            if (val && val.audit_reason) {
                this.isShowFailDialog = true;
            }
        }
    },
    computed: {
        ...mapState({
            latestId: state => state.Service.aiContract.latestId,
            generatingId: state => state.Service.aiContract.generatingId,
            contractInfo: state => state.Service.aiContract.contractInfo
        }),
        failText() {
            const {contractInfo, createFailText} = this;
            return createFailText || contractInfo?.audit_reason || '';
        }
    },
    mounted() {
        this.getGenerateCount();
    },
    unmounted() {
        clearTimeout(this.getProgressTimer);
    },
    methods: {
        ...mapActions({
            getCurrentContract: 'Service/getCurrentContract'
        }),
        ...mapMutations({
            setAIContract: 'Service/setAIContract'
        }),
        // 获取生成数量
        getGenerateCount() {
            this.$http
                .get('/bp/api/aicontract/count', {
                    params: {
                        // 查询生成中（0）和生成成功（1）的合同数量
                        status: '0,1'
                    }
                })
                .then(res => {
                    if (+res.code === 0 && res.data) {
                        const {create_count, day_create_total} = res.data;
                        if (day_create_total >= create_count) {
                            this.surplus = day_create_total - create_count;
                        }
                    }
                });
        },
        // 生成合同
        handleGenerate() {
            if (this.isGenerating) {
                this.$Message.info('正在生成中，请稍等');
                return;
            }
            if (!this.surplus) {
                this.$Message.info('今天的使用次数已经用完啦～明天再来吧～');
                return;
            }
            const {type, query} = this;
            if (query.length > this.maxlength) {
                this.$Message.error(`具体要求不能超过${this.maxlength}个字`);
                return;
            }
            this.isGenerating = true;
            this.$http
                .post('/bp/api/aicontract/create', {
                    contract_type: type,
                    query
                })
                .then(res => {
                    this.$wywLog.send({
                        serverId: 17047,
                        action: 'click',
                        area: 'btn-sc',
                        success: +res?.code === 0 ? 1 : 0,
                        content: encodeURIComponent(type + '|' + query)
                    });
                    if (+res?.code === 0 && res?.data?.id) {
                        this.getCurrentContract({id: res.data.id});
                    }
                    else {
                        this.createFailText
                            = res?.msg || '太多人需要小助手啦～请稍后再试';
                        this.isShowFailDialog = true;
                        this.isGenerating = false;
                    }
                })
                .catch(err => {
                    this.createFailText = '太多人需要小助手啦～请稍后再试';
                    this.isGenerating = false;
                });
        },
        // 获取进度条数字
        getProgressValue() {
            clearTimeout(this.getProgressTimer);
            this.getProgressTimer = setInterval(() => {
                this.progressValue = this.$refs.loadingBar?.value || 0;
            }, 100);
        },
        handleDownload() {
            if (this.isDownloading || !this.contractInfo) {
                return;
            }
            this.isDownloading = true;

            this.$wywLog.send({
                serverId: 17047,
                action: 'click',
                area: 'btn-xzword'
            });
            const {bos_link, contract_type, update_time} = this.contractInfo;
            getContractDocLink(this.contractInfo)
                .then(bosLink => {
                    downloadFile(
                        bosLink,
                        `${contract_type}-${dateFormat(update_time)}`
                    );
                    // 将新获取到的链接写回store
                    if (!bos_link) {
                        this.setAIContract({
                            contractInfo: {
                                ...this.contractInfo,
                                bos_link: bosLink
                            }
                        });
                    }
                })
                .catch(msg => {
                    this.$Message.error(msg || '文件获取失败，请稍后再试');
                })
                .finally(() => {
                    this.isDownloading = false;
                });
        },
        onFocus() {
            this.$wywLog.send({
                serverId: 17047,
                action: 'click',
                area: 'bcyq-sr'
            });
        }
    }
};
</script>
<style lang="less" scoped>
.generate-container {
    display: flex;
    align-items: stretch;

    .form-area {
        flex-shrink: 0;
        width: 439px;
        padding: 5px 30px 22px;
        border-right: 1px solid #e9ecf3;

        .field {
            margin-top: 23px;
            .label {
                font-family: PingFangSC-Medium;
                font-size: 16px;
                color: #333333;
                line-height: 18px;
                margin-bottom: 16px;
            }

            .tips {
                font-family: PingFangSC-Regular;
                font-size: 12px;
                color: #9d9d9d;
                line-height: 17px;
            }

            /deep/ .veui-textarea {
                width: 100%;
                min-height: 60px;
                max-height: 152px;
                .veui-textarea-count {
                    color: #999;
                    background: transparent;
                }
                &.veui-disabled textarea:disabled {
                    background: #fff;
                    color: #666;
                }
            }
        }
        .submit-btn {
            cursor: pointer;
            margin-top: 24px;
            width: 100%;
            padding: 6px 0 10px;
            background: url(https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/button-bg.png)
                no-repeat center / cover;
            border-radius: 8px;
            .btn-text {
                font-family: PingFangSC-Semibold;
                font-size: 18px;
                color: #ffffff;
                text-align: center;
                line-height: 28px;
            }
            .btn-tip {
                font-family: PingFangSC-Regular;
                font-size: 12px;
                color: #ffffff;
                text-align: center;
                line-height: 14px;
            }

            &.disabled {
                background: url(https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/button-bg-disabled.png)
                    no-repeat center / cover;
            }
        }
    }

    .result-area {
        flex: 1 1 0;
        position: relative;

        .result-wrapper {
            height: 100%;
            position: relative;
            .header {
                position: relative;
                display: flex;
                align-items: center;
                height: 50px;
                padding: 0 20px 0 22px;
                &::after {
                    content: "";
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    width: 100%;
                    height: 22px;
                    box-shadow: 0 3px 5px 0 rgba(215, 220, 233, 0.43);
                    z-index: 1;
                }
                h3 {
                    font-family: PingFangSC-Medium;
                    font-size: 14px;
                    color: #333333;
                }
                .time {
                    margin-left: 17px;
                    font-family: PingFangSC-Regular;
                    font-size: 12px;
                    color: #9d9d9d;
                }
                .download-btn {
                    margin-left: auto;
                    cursor: pointer;
                    height: 32px;
                    padding: 0 10px;
                    display: flex;
                    align-items: center;
                    border: 1px solid #0759ff;
                    border-radius: 4px;
                    font-family: PingFangSC-Medium;
                    font-size: 12px;
                    color: #0759ff;
                    &:hover {
                        background-color: rgba(56, 85, 213, 0.08);
                    }

                    &::before {
                        content: "";
                        width: 16px;
                        height: 16px;
                        background: url(https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/icon-word.png)
                            no-repeat center / cover;
                        margin-right: 2px;
                    }
                }
            }
            .content {
                position: absolute;
                top: 50px;
                left: 0;
                width: 100%;
                height: calc(100% - 50px);
                font-family: PingFangSC-Regular;
                font-size: 14px;
                color: #000000;
                padding: 26px 36px;
                overflow-y: auto;
                white-space: pre-wrap;
            }
        }

        .result-step {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            font-family: PingFangSC-Regular;
            font-size: 14px;
            color: #000000;
            text-align: center;
            line-height: 14px;
            .generating-pic {
                margin-top: 266px;
                margin-bottom: 18px;
                width: 86px;
                height: 86px;
                background: url("https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/generate-ing.gif")
                    no-repeat center / cover;
            }
            .fail-pic {
                margin-top: 266px;
                margin-bottom: 18px;
                width: 86px;
                height: 86px;
                background: url("https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/generate-fail.png")
                    no-repeat center / cover;
            }
            /deep/ .veui-progress-bar {
                width: 170px;
                margin-top: 18px;
                .veui-progress-meter {
                    background-color: #0055ff;
                }
            }
        }
        .empty {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            font-family: PingFangSC-Medium;
            font-size: 14px;
            color: #666666;
            text-align: center;
            line-height: 14px;
            .empty-pic {
                margin-top: 206px;
                margin-bottom: 18px;
                width: 180px;
                height: 144px;
                background: url("https://lvlin-base.cdn.bcebos.com/fe-saas/page/ai-contract/empty.png")
                    no-repeat center / cover;
            }
        }
    }
}

.confirm-veui-dialog {
    /deep/ .veui-dialog-content-foot {
        .veui-button:nth-last-child(1) {
            display: none;
        }
    }
}
</style>
